import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

export interface SEOLandingPageProps {
  title: string;
  metaDescription: string;
  ogTitle: string;
  h1: string;
  intro: string;
  paragraphs: string[];
  features: string[];
  playStoreUrl: string;
  icon: string;
  appName: string;
  accentColor: string;
  canonicalPath: string;
  // Optional rich content — all backward compatible. Pages can opt in.
  howToTitle?: string;
  howToSteps?: { name: string; text: string }[];
  useCases?: { title: string; body: string }[];
  faqs?: { q: string; a: string }[];
  related?: { label: string; path: string; tagline?: string }[];
}

const PlayStoreIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SEOLandingPage: React.FC<SEOLandingPageProps> = ({
  title,
  metaDescription,
  ogTitle,
  h1,
  intro,
  paragraphs,
  features,
  playStoreUrl,
  icon,
  appName,
  accentColor,
  canonicalPath,
  howToTitle,
  howToSteps,
  useCases,
  faqs,
  related,
}) => {
  const canonical = `https://cosmovexapps.com${canonicalPath}`;

  // Schema.org graph — one MobileApplication always, plus optional FAQPage + HowTo
  // when the page supplies that data. Eligible for rich-result snippets.
  const graph: any[] = [
    {
      "@type": "MobileApplication",
      name: appName,
      description: metaDescription,
      operatingSystem: "Android",
      applicationCategory: "DeveloperApplication",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      url: playStoreUrl,
      author: {
        "@type": "Organization",
        name: "Cosmovex",
        url: "https://cosmovexapps.com",
      },
    },
  ];
  if (faqs && faqs.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  if (howToSteps && howToSteps.length) {
    graph.push({
      "@type": "HowTo",
      name: howToTitle || `How to use ${appName} on Android`,
      step: howToSteps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    });
  }
  const structuredData = { "@context": "https://schema.org", "@graph": graph };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FBFAF6",
        color: "#26313B",
        fontFamily:
          "'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content={`https://cosmovexapps.com${icon}`}
        />
        <meta property="og:site_name" content="Cosmovex" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* ── NAV ── */}
      <header
        style={{
          padding: "1.25rem 2rem",
          borderBottom: "1px solid #E1DCCE",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backdropFilter: "blur(12px)",
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(251,250,246,0.85)",
        }}
      >
        <Link
          to="/"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            textDecoration: "none",
          }}
        >
          <div
            style={{
              width: "36px",
              height: "36px",
              background: "#E8B44C",
              borderRadius: "0.6rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 900,
              color: "#221A06",
              fontSize: "1rem",
            }}
          >
            C
          </div>
          <span style={{ color: "#26313B", fontWeight: 700, fontSize: "1.1rem" }}>
            Cosmovex
          </span>
        </Link>
        <a
          href={playStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.55rem 1.25rem",
            background: "#E8B44C",
            color: "#221A06",
            border: "1px solid #D9A32E",
            borderRadius: "0.75rem",
            fontWeight: 600,
            fontSize: "0.875rem",
            textDecoration: "none",
          }}
        >
          <PlayStoreIcon />
          Get App
        </a>
      </header>

      {/* ── HERO ── */}
      <section
        style={{
          padding: "5rem 2rem 3.5rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* grid bg */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.03,
            backgroundImage: `linear-gradient(rgba(52,44,26,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(52,44,26,0.5) 1px,transparent 1px)`,
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        {/* glow */}
        <div
          style={{
            position: "absolute",
            top: "-30%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "700px",
            height: "500px",
            background: "transparent",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            position: "relative",
            zIndex: 10,
          }}
        >
          {/* App identity row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            <img
              src={icon}
              alt={`${appName} icon`}
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "1rem",
                border: "1px solid #E1DCCE",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
            <div>
              <div
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  color: "#A8720E",
                  marginBottom: "0.2rem",
                }}
              >
                Cosmovex
              </div>
              <span
                style={{
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  color: "#5B6B7C",
                }}
              >
                {appName}
              </span>
            </div>
          </div>

          {/* H1 */}
          <h1
            style={{
              fontSize: "clamp(2.2rem, 5.5vw, 4rem)",
              fontWeight: 800,
              lineHeight: 1.08,
              letterSpacing: "-0.03em",
              marginBottom: "1.5rem",
              color: "#26313B",
            }}
          >
            {h1}
          </h1>

          {/* Intro */}
          <p
            style={{
              color: "#3D4B59",
              fontSize: "clamp(1.05rem, 2vw, 1.25rem)",
              lineHeight: 1.75,
              maxWidth: "720px",
              marginBottom: "2.5rem",
            }}
          >
            {intro}
          </p>

          {/* CTA */}
          <a
            href={playStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.6rem",
              padding: "1rem 2.2rem",
              background: "#E8B44C",
              color: "#221A06",
              border: "1px solid #D9A32E",
              borderRadius: "1rem",
              fontWeight: 700,
              fontSize: "1.05rem",
              textDecoration: "none",
              boxShadow: "0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.transform =
                "scale(1.03)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.transform =
                "scale(1)";
            }}
          >
            <PlayStoreIcon />
            Free on Google Play
          </a>
        </div>
      </section>

      {/* ── CONTENT ── */}
      <section style={{ padding: "3rem 2rem", maxWidth: "900px", margin: "0 auto" }}>
        {paragraphs.map((para, i) => (
          <p
            key={i}
            style={{
              color: "#3D4B59",
              fontSize: "1.05rem",
              lineHeight: 1.85,
              marginBottom: "1.5rem",
            }}
          >
            {para}
          </p>
        ))}
      </section>

      {/* ── FEATURES ── */}
      <section
        style={{
          padding: "3rem 2rem 5rem",
          background: "#F1EEE5",
          borderTop: "1px solid #E1DCCE",
          borderBottom: "1px solid #E1DCCE",
        }}
      >
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              fontWeight: 800,
              color: "#26313B",
              marginBottom: "2rem",
              letterSpacing: "-0.02em",
            }}
          >
            What you get
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "1rem",
            }}
          >
            {features.map((feature, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.75rem",
                  padding: "1.1rem 1.25rem",
                  borderRadius: "0.875rem",
                  background: "#FFFFFF",
                  border: "1px solid #E1DCCE",
                }}
              >
                <span
                  style={{
                    color: "#A8720E",
                    flexShrink: 0,
                    marginTop: "1px",
                  }}
                >
                  <CheckIcon />
                </span>
                <span
                  style={{
                    color: "#3D4B59",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  {feature}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW TO ── */}
      {howToSteps && howToSteps.length > 0 && (
        <section style={{ padding: "4rem 2rem", maxWidth: "900px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              fontWeight: 800,
              color: "#26313B",
              marginBottom: "2rem",
              letterSpacing: "-0.02em",
            }}
          >
            {howToTitle || `How to use ${appName}`}
          </h2>
          <ol style={{ listStyle: "none", padding: 0, margin: 0, counterReset: "step" }}>
            {howToSteps.map((step, i) => (
              <li
                key={i}
                style={{
                  display: "flex",
                  gap: "1.25rem",
                  marginBottom: "1.5rem",
                  padding: "1.25rem 1.5rem",
                  background: "#FFFFFF",
                  border: "1px solid #E1DCCE",
                  borderRadius: "0.875rem",
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    width: "2.25rem",
                    height: "2.25rem",
                    borderRadius: "50%",
                    background: "#E8B44C",
                    color: "#221A06",
                    fontWeight: 800,
                    fontSize: "1rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {i + 1}
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#26313B",
                      marginBottom: "0.4rem",
                    }}
                  >
                    {step.name}
                  </h3>
                  <p style={{ color: "#3D4B59", fontSize: "0.98rem", lineHeight: 1.7, margin: 0 }}>
                    {step.text}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* ── USE CASES ── */}
      {useCases && useCases.length > 0 && (
        <section
          style={{
            padding: "4rem 2rem",
            background: "#F1EEE5",
            borderTop: "1px solid #E1DCCE",
            borderBottom: "1px solid #E1DCCE",
          }}
        >
          <div style={{ maxWidth: "900px", margin: "0 auto" }}>
            <h2
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 800,
                color: "#26313B",
                marginBottom: "2rem",
                letterSpacing: "-0.02em",
              }}
            >
              Who uses {appName}
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "1.25rem",
              }}
            >
              {useCases.map((uc, i) => (
                <div
                  key={i}
                  style={{
                    padding: "1.5rem",
                    borderRadius: "0.875rem",
                    background: "#FFFFFF",
                    border: "1px solid #E1DCCE",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: "#A8720E",
                      marginBottom: "0.6rem",
                    }}
                  >
                    {uc.title}
                  </h3>
                  <p style={{ color: "#3D4B59", fontSize: "0.95rem", lineHeight: 1.7, margin: 0 }}>
                    {uc.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ── */}
      {faqs && faqs.length > 0 && (
        <section style={{ padding: "4rem 2rem", maxWidth: "900px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              fontWeight: 800,
              color: "#26313B",
              marginBottom: "2rem",
              letterSpacing: "-0.02em",
            }}
          >
            Frequently asked questions
          </h2>
          <div>
            {faqs.map((f, i) => (
              <details
                key={i}
                style={{
                  borderRadius: "0.875rem",
                  background: "#FFFFFF",
                  border: "1px solid #E1DCCE",
                  padding: "1.1rem 1.25rem",
                  marginBottom: "0.75rem",
                }}
              >
                <summary
                  style={{
                    fontSize: "1rem",
                    fontWeight: 600,
                    color: "#26313B",
                    cursor: "pointer",
                    listStyle: "none",
                  }}
                >
                  {f.q}
                </summary>
                <p
                  style={{
                    color: "#3D4B59",
                    fontSize: "0.98rem",
                    lineHeight: 1.75,
                    marginTop: "0.85rem",
                    marginBottom: 0,
                  }}
                >
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* ── RELATED TOOLS ── */}
      {related && related.length > 0 && (
        <section
          style={{
            padding: "4rem 2rem",
            background: "#F1EEE5",
            borderTop: "1px solid #E1DCCE",
            borderBottom: "1px solid #E1DCCE",
          }}
        >
          <div style={{ maxWidth: "900px", margin: "0 auto" }}>
            <h2
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 800,
                color: "#26313B",
                marginBottom: "2rem",
                letterSpacing: "-0.02em",
              }}
            >
              Related tools
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                gap: "1rem",
              }}
            >
              {related.map((r, i) => (
                <Link
                  key={i}
                  to={r.path}
                  style={{
                    display: "block",
                    padding: "1.25rem 1.4rem",
                    borderRadius: "0.875rem",
                    background: "#FFFFFF",
                    border: "1px solid #E1DCCE",
                    textDecoration: "none",
                    transition: "border-color 0.2s, transform 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = "#D9A32E";
                    (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = "#E1DCCE";
                    (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)";
                  }}
                >
                  <div
                    style={{
                      color: "#26313B",
                      fontWeight: 700,
                      fontSize: "1rem",
                      marginBottom: r.tagline ? "0.35rem" : 0,
                    }}
                  >
                    {r.label}
                  </div>
                  {r.tagline && (
                    <div style={{ color: "#5B6B7C", fontSize: "0.88rem", lineHeight: 1.5 }}>
                      {r.tagline}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── BOTTOM CTA ── */}
      <section
        style={{
          padding: "5rem 2rem",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
              fontWeight: 800,
              color: "#26313B",
              marginBottom: "1rem",
              letterSpacing: "-0.02em",
            }}
          >
            Ready to try {appName}?
          </h2>
          <p
            style={{
              color: "#5B6B7C",
              fontSize: "1.05rem",
              marginBottom: "2rem",
              lineHeight: 1.7,
            }}
          >
            Free to download. No subscription required to get started.
          </p>
          <a
            href={playStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.6rem",
              padding: "1rem 2.5rem",
              background: "#E8B44C",
              color: "#221A06",
              border: "1px solid #D9A32E",
              borderRadius: "1rem",
              fontWeight: 700,
              fontSize: "1.1rem",
              textDecoration: "none",
              boxShadow: "0 1px 2px rgba(52,44,26,.06), 0 4px 14px rgba(52,44,26,.05)",
            }}
          >
            <PlayStoreIcon />
            Download Free on Google Play
          </a>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer
        style={{
          padding: "2rem",
          borderTop: "1px solid #E1DCCE",
          background: "#F1EEE5",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <Link
          to="/"
          style={{
            color: "#5B6B7C",
            textDecoration: "none",
            fontSize: "0.9rem",
          }}
        >
          &larr; Back to Cosmovex
        </Link>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1.25rem",
            justifyContent: "center",
          }}
        >
          {[
            ["/blogs", "Blog"],
            ["/about", "About"],
            ["/contact", "Contact"],
            ["/privacy-policy", "Privacy Policy"],
            ["/terms-of-service", "Terms of Service"],
          ].map(([to, label]) => (
            <Link
              key={to}
              to={to}
              style={{ color: "#0F6BBF", textDecoration: "none", fontSize: "0.9rem" }}
            >
              {label}
            </Link>
          ))}
        </div>
        <p style={{ color: "#8A93A2", fontSize: "0.85rem" }}>
          &copy; {new Date().getFullYear()} Cosmovex. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default SEOLandingPage;
